/**
 * RulePreviewMediaBubble - Bolha de mídia previewável para o card de preview de ação.
 *
 * Extraído de RulePreviewActionCard.tsx (v2.1.0) para respeitar o boundary do
 * React Fast Refresh: cada arquivo deve exportar no máximo UM componente com hooks.
 * Como MediaBubble usa useState e RulePreviewActionCard já é um componente com hooks
 * exportado, mantê-los no mesmo arquivo causava "ReferenceError: MediaBubble is not
 * defined" após HMR do Vite.
 *
 * @version 1.0.1
 * @date 2026-07-09
 *
 * CHANGELOG:
 *   v1.0.1 - Preview agora resolve caminhos relativos da Galeria de Mídia
 *            via getStorageUrl antes de validar/reproduzir. Corrige mídias
 *            internas salvas como storage_path (ex.: audio .ogg) que ficavam
 *            estáticas no preview do chat.
 *   v1.0.0 - Extração inicial do componente para evitar falhas de HMR.
 */

import { useState } from 'react';
import { Play, Eye, Download, X, Music } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ImageLightbox } from '@/components/tenant/chat/ImageLightbox';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { OutgoingBubble, ReadTicks } from './RulePreviewActionCard';
import { getStorageUrl } from '@/lib/storage';

/**
 * Resolve URL externa ou caminho relativo do Supabase Storage para uma URL navegável.
 * Isso mantém compatibilidade com mídias da galeria, que salvam apenas storage_path.
 */
function resolvePreviewUrl(url: string | undefined): string | undefined {
  const normalizedUrl = url?.trim();
  if (!normalizedUrl) return undefined;

  return getStorageUrl(normalizedUrl) || normalizedUrl;
}

/** Valida se a URL resolvida pode ser previewada (http/https) sem quebrar o modal */
function isPreviewableUrl(url: string | undefined): boolean {
  const resolvedUrl = resolvePreviewUrl(url);
  if (!resolvedUrl) return false;

  try {
    const u = new URL(resolvedUrl);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Renderiza a bolha WhatsApp de mídia e permite abrir a mídia real:
 *  - Imagem/Vídeo/Documento: abre ImageLightbox
 *  - Áudio: player inline + Dialog dedicado com download
 */
export function MediaBubble({
  actionType,
  mediaUrl,
  caption,
  filename,
  time,
  Icon,
}: {
  actionType: string;
  mediaUrl?: string;
  caption?: string;
  filename: string;
  time: string;
  Icon: React.ElementType;
}) {
  // Estados locais para abertura do lightbox/dialog
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [audioDialogOpen, setAudioDialogOpen] = useState(false);

  const isImage = actionType === 'send_image';
  const isVideo = actionType === 'send_video';
  const isAudio = actionType === 'send_audio';
  const isDocument = actionType === 'send_document';

  // Caminhos internos da galeria chegam como storage_path; resolvemos antes de renderizar.
  const previewUrl = resolvePreviewUrl(mediaUrl);

  // Só habilita preview quando a URL resolvida é válida (http/https).
  const canPreview = isPreviewableUrl(mediaUrl);

  // Mapeia tipo para o modo aceito pelo ImageLightbox
  const lightboxType: 'image' | 'video' | 'document' | null = isImage
    ? 'image'
    : isVideo
    ? 'video'
    : isDocument
    ? 'document'
    : null;

  // Handler unificado para abrir o preview (evita propagar clique para o container)
  const handleOpenPreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canPreview) return;
    if (isAudio) setAudioDialogOpen(true);
    else setLightboxOpen(true);
  };

  return (
    <>
      <OutgoingBubble time={time} noPadding showInlineTime={false}>
        <div className="p-1">
          {/* Área de thumbnail/preview — clicável quando previewável */}
          <button
            type="button"
            onClick={handleOpenPreview}
            disabled={!canPreview}
            className={cn(
              'rounded flex items-center justify-center w-full mb-1 relative overflow-hidden',
              'bg-slate-200/60 dark:bg-slate-700/40',
              isImage ? 'aspect-square min-w-[220px]' : 'min-h-[80px] min-w-[220px]',
              canPreview ? 'cursor-pointer hover:opacity-90 transition' : 'cursor-default',
            )}
            aria-label={canPreview ? 'Visualizar mídia' : 'Mídia sem preview disponível'}
          >
            {isImage && canPreview ? (
              <img src={previewUrl} alt={filename} className="w-full h-full object-cover" />
            ) : isVideo && canPreview ? (
              <>
                {/* Frame do vídeo como thumbnail + overlay de play */}
                <video
                  src={previewUrl}
                  className="w-full h-full object-cover"
                  preload="metadata"
                  muted
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <Play className="h-8 w-8 text-white" />
                </div>
              </>
            ) : (
              <Icon className="h-10 w-10 text-slate-400" />
            )}
          </button>

          <div className="px-2 pb-1">
            <p className="text-[11px] font-semibold text-emerald-800 dark:text-emerald-300 uppercase truncate">
              {filename}
            </p>
            {caption && (
              <p className="text-[12px] text-slate-700 dark:text-slate-200 mt-0.5 line-clamp-2">
                {caption}
              </p>
            )}

            {/* Player inline para áudio — permite escutar direto do card */}
            {isAudio && canPreview && (
              <audio
                src={previewUrl}
                controls
                className="mt-1.5 h-8 w-full"
                onClick={(e) => e.stopPropagation()}
              />
            )}

            {/* Botão "Visualizar" para documento/vídeo (imagem já é clicável na thumb) */}
            {canPreview && (isDocument || isVideo) && (
              <button
                type="button"
                onClick={handleOpenPreview}
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-800 dark:text-emerald-300 hover:underline"
              >
                <Eye className="h-3 w-3" />
                Visualizar
              </button>
            )}

            <div className="mt-1 flex items-center justify-end gap-1">
              <span className="text-[10px] text-slate-500 dark:text-slate-300/70 uppercase">
                {time}
              </span>
              <ReadTicks />
            </div>
          </div>
        </div>
      </OutgoingBubble>

      {/* Lightbox para imagem/vídeo/documento — reusa componente do chat */}
      {lightboxType && canPreview && (
        <ImageLightbox
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          mediaItems={[{ url: previewUrl || mediaUrl || '', type: lightboxType, title: filename }]}
        />
      )}

      {/* Dialog dedicado para áudio (ImageLightbox não suporta) */}
      {isAudio && canPreview && (
        <Dialog open={audioDialogOpen} onOpenChange={setAudioDialogOpen}>
          <DialogContent className="max-w-md">
            <VisuallyHidden>
              <DialogTitle>{filename || 'Áudio'}</DialogTitle>
            </VisuallyHidden>
            <div className="flex items-center gap-3 mb-3">
              <Music className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium truncate flex-1">{filename}</p>
            </div>
            <audio src={previewUrl} controls autoPlay className="w-full" />
            <div className="mt-4 flex justify-end gap-2">
              <Button asChild variant="outline" size="sm">
                <a href={previewUrl} download target="_blank" rel="noopener noreferrer">
                  <Download className="h-4 w-4 mr-1.5" />
                  Baixar
                </a>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setAudioDialogOpen(false)}>
                <X className="h-4 w-4 mr-1.5" />
                Fechar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
